import { useState } from "react";
import { toast } from "sonner";

import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useReissueServiceKeys, useSetLlmMode } from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { logger } from "@/lib/logger";
import { OVERLAP_HOURS } from "@/lib/services-gateway";

import { ConfirmButton } from "./confirm-button";

/** Issue every key again and redeploy dev; 0 h overlap for a suspected leak. Admin only. */
export function ReissueKeys({ projectId }: { projectId: number }) {
  const reissue = useReissueServiceKeys(projectId);
  const [overlap, setOverlap] = useState("24");

  const handleConfirm = async () => {
    try {
      const result = await reissue.mutateAsync({ overlapHours: Number(overlap) });
      const problems = result?.problems ?? [];
      if (problems.length > 0) {
        toast.warning(`Keys re-issued with problems: ${problems.join("; ")}`);
      } else {
        toast.success(
          result?.redeployed
            ? "Keys re-issued. Dev is redeploying."
            : "Keys re-issued. Redeploy dev to use them.",
        );
      }
    } catch (err) {
      logger.error("[services] re-issue failed:", err);
      toast.error(readApiErrorMessage(err, "Could not re-issue the keys. Please try again."));
    }
  };

  return (
    <ConfirmButton
      label={reissue.isPending ? "Re-issuing…" : "Re-issue keys"}
      title="Re-issue every key?"
      description="Every service gets a new key, written to the repository, and dev redeploys. The old keys keep working for the time you choose; choose 0 hours if a key may have leaked."
      confirmLabel="Re-issue"
      onConfirm={() => void handleConfirm()}
      disabled={reissue.isPending}
      destructive
    >
      <div className="space-y-1.5">
        <Label htmlFor="reissue-overlap">Old keys keep working for</Label>
        <Select value={overlap} onValueChange={setOverlap}>
          <SelectTrigger id="reissue-overlap">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {OVERLAP_HOURS.map((hours) => (
              <SelectItem key={hours} value={String(hours)}>
                {hours === 0 ? "0 hours (suspected leak: stop them now)" : `${hours} hours`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </ConfirmButton>
  );
}

/** Switch the models between the gateway key and the app's own Foundry access. Admin only. */
export function LlmModeSwitch({
  projectId,
  mode,
}: {
  projectId: number;
  mode: "gateway" | "direct";
}) {
  const setMode = useSetLlmMode(projectId);
  const target = mode === "gateway" ? "direct" : "gateway";

  const handleConfirm = async () => {
    try {
      const result = await setMode.mutateAsync({ mode: target });
      const problems = result?.problems ?? [];
      if (problems.length > 0) {
        toast.warning(`Switched, with problems: ${problems.join("; ")}`);
      } else {
        toast.success(
          result?.redeployed
            ? "Switched. Dev is redeploying."
            : "Switched. Redeploy dev to finish.",
        );
      }
    } catch (err) {
      logger.error("[services] LLM mode switch failed:", err);
      toast.error(readApiErrorMessage(err, "Could not switch. Please try again."));
    }
  };

  return (
    <div className="flex justify-end">
      <ConfirmButton
        label={
          setMode.isPending
            ? "Switching…"
            : target === "direct"
              ? "Use Foundry directly"
              : "Use the gateway for the models"
        }
        title={target === "direct" ? "Use Foundry directly?" : "Use the gateway for the models?"}
        description={
          target === "direct"
            ? "The app and its creator get Foundry access first, then the app's model key is removed and dev redeploys. No budget applies to the models after this."
            : "The app gets a new model key and dev redeploys; that deploy removes the app's own Foundry access. The model budget applies again."
        }
        confirmLabel="Switch"
        onConfirm={() => void handleConfirm()}
        disabled={setMode.isPending}
        destructive
      />
    </div>
  );
}
